import { useEffect, useRef, useState } from 'react'
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/outline'
import { PaperAirplaneIcon } from '@heroicons/react/24/solid'

interface Props {
  onSend: (text: string) => void
  onStop?: () => void
  disabled: boolean
  historySizeBytes?: number
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return Math.round(bytes / (1024 * 1024)) + ' MB'
  return Math.round(bytes / 1024) + ' KB'
}

// Browser Speech Recognition types (not always present in TS DOM lib)
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
}
interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  onresult: ((e: SpeechRecognitionEvent) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
}
declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition
    webkitSpeechRecognition?: new () => SpeechRecognition
  }
}

export default function MessageInput({ onSend, onStop, disabled, historySizeBytes = 0 }: Props) {
  const [text, setText] = useState('')
  const [listening, setListening] = useState(false)
  const [voiceSupported, setVoiceSupported] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const SR = window.SpeechRecognition ?? window.webkitSpeechRecognition
    if (SR) {
      setVoiceSupported(true)
      const rec = new SR()
      rec.continuous = false
      rec.interimResults = true
      rec.lang = 'en-US'
      rec.onresult = (e: SpeechRecognitionEvent) => {
        const transcript = Array.from(e.results).map((r) => r[0].transcript).join('')
        setText(transcript)
      }
      rec.onend = () => setListening(false)
      rec.onerror = () => setListening(false)
      recognitionRef.current = rec
    }
  }, [])

  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = Math.min(ta.scrollHeight, 140) + 'px'
  }, [text])

  function toggleVoice() {
    const rec = recognitionRef.current
    if (!rec) return
    if (listening) {
      rec.stop()
    } else {
      setText('')
      rec.start()
      setListening(true)
    }
  }

  function submit() {
    const trimmed = text.trim()
    if (!trimmed) return
    onSend(trimmed)
    setText('')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <div style={{ padding: '10px 12px', background: 'var(--bg)' }}>
      {/* Outer rounded box — the whole thing is the "input" */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: '12px',
          background: 'var(--surface)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Text area */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={listening ? 'Listening...' : 'Message...'}
          disabled={listening}
          rows={1}
          style={{
            resize: 'none',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: '12px 14px 8px',
            color: 'var(--text)',
            fontSize: '15px',
            lineHeight: '1.5',
            overflowY: 'hidden',
          }}
        />

        {/* Bottom bar with buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '4px',
            padding: '6px 8px',
          }}
        >
          {historySizeBytes >= 1024 && (
            <button
              onClick={() => setShowHistoryModal(true)}
              style={{
                marginRight: 'auto',
                background: 'none',
                border: 'none',
                padding: '2px 4px',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                fontSize: '11px',
                opacity: 0.7,
              }}
            >
              {formatBytes(historySizeBytes)}
            </button>
          )}
          {/* Stop button — visible only while loading */}
          {disabled && onStop && (
            <button
              onClick={onStop}
              aria-label="Stop"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <StopIcon style={{ width: 18, height: 18, color: 'var(--text-muted)' }} />
            </button>
          )}

          {/* Mic button */}
          {voiceSupported ? (
            <button
              onClick={toggleVoice}
              disabled={false}
              aria-label={listening ? 'Stop recording' : 'Start voice input'}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: listening ? 'var(--danger)' : 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                cursor: 'pointer',
                transition: 'background 0.2s',
                opacity: 1,
              }}
            >
              {listening
                ? <StopIcon style={{ width: 18, height: 18, color: 'var(--text-muted)' }} />
                : <MicrophoneIcon style={{ width: 18, height: 18, color: 'var(--text-muted)' }} />}
            </button>
          ) : null}

          {/* Send button */}
          <button
            onClick={submit}
            disabled={!text.trim()}
            aria-label="Send message"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'transparent',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              cursor: text.trim() ? 'pointer' : 'default',
              opacity: text.trim() ? 1 : 0.35,
              color: 'var(--text)',
              transition: 'opacity 0.15s',
            }}
          >
            <PaperAirplaneIcon style={{ width: 18, height: 18, color: 'var(--text-muted)' }} />
          </button>
        </div>
      </div>
      {showHistoryModal && (
        <div
          onClick={() => setShowHistoryModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '20px 24px',
              maxWidth: '320px',
              width: '90%',
              color: 'var(--text)',
              fontSize: '14px',
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '10px' }}>
              Conversation history: {formatBytes(historySizeBytes)}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
              This is the total size of the conversation sent along with each prompt. Consider refreshing the page to save tokens if the history is no longer needed.
            </div>
            <button
              onClick={() => setShowHistoryModal(false)}
              style={{
                marginTop: '16px',
                width: '100%',
                padding: '8px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                background: 'none',
                color: 'var(--text)',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
